import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";

export const Viewport = forwardRef(function Viewport({ url, wireframe, background, onLoaded, onError }, ref) {
  const host = useRef(null);
  const ctx = useRef({});

  useEffect(() => {
    const el = host.current;
    const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    el.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 10000);
    camera.up.set(0, 0, 1);
    camera.position.set(140, -180, 160);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    scene.add(new THREE.HemisphereLight(0xffffff, 0x1a1a1a, 0.55));
    const key = new THREE.DirectionalLight(0xffffff, 2.4);
    key.position.set(260, -140, 120);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0xff7a33, 0.6);
    rim.position.set(-200, 200, 80);
    scene.add(rim);
    const grid = new THREE.GridHelper(260, 26, 0x4a4a4a, 0x262626);
    grid.rotation.x = Math.PI / 2;
    scene.add(grid);
    const resize = () => {
      const { clientWidth: w, clientHeight: h } = el;
      renderer.setSize(w, h);
      camera.aspect = w / Math.max(h, 1);
      camera.updateProjectionMatrix();
    };
    const ro = new ResizeObserver(resize);
    ro.observe(el);
    resize();
    let raf;
    const loop = () => {
      controls.update();
      renderer.render(scene, camera);
      raf = requestAnimationFrame(loop);
    };
    loop();
    ctx.current = { renderer, scene, camera, controls, grid };
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      controls.dispose();
      renderer.dispose();
      el.removeChild(renderer.domElement);
    };
  }, []);

  const fit = (view = "perspective") => {
    const { camera, controls, mesh } = ctx.current;
    if (!camera) return;
    const box = mesh ? new THREE.Box3().setFromObject(mesh) : new THREE.Box3(new THREE.Vector3(-50, -50, 0), new THREE.Vector3(50, 50, 10));
    const c = box.getCenter(new THREE.Vector3());
    const r = box.getSize(new THREE.Vector3()).length() / 2 || 50;
    const d = r / Math.sin((camera.fov * Math.PI) / 360) * 1.05;
    const dirs = {
      perspective: [0.55, -0.7, 0.6],
      front: [0, -1, 0.0001],
      side: [1, 0, 0.0001],
      top: [0, -0.0001, 1],
    };
    const v = new THREE.Vector3(...dirs[view]).normalize().multiplyScalar(d);
    camera.position.copy(c.clone().add(v));
    controls.target.copy(c);
    controls.update();
  };

  useImperativeHandle(ref, () => ({
    setView: fit,
    reset: () => fit("perspective"),
    zoom: (f) => {
      const { camera, controls } = ctx.current;
      const off = camera.position.clone().sub(controls.target).multiplyScalar(f);
      camera.position.copy(controls.target.clone().add(off));
    },
    snapshot: () => ctx.current.renderer?.domElement.toDataURL("image/jpeg", 0.6),
  }));

  useEffect(() => {
    const { scene } = ctx.current;
    if (scene) scene.background = new THREE.Color(background);
  }, [background]);

  useEffect(() => {
    const { mesh } = ctx.current;
    if (mesh) mesh.material.wireframe = wireframe;
  }, [wireframe]);

  useEffect(() => {
    const { scene } = ctx.current;
    if (!scene) return;
    if (ctx.current.mesh) {
      scene.remove(ctx.current.mesh);
      ctx.current.mesh.geometry.dispose();
      ctx.current.mesh = null;
    }
    if (!url) return;
    let cancelled = false;
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error("Model file is no longer available");
        return r.arrayBuffer();
      })
      .then((buf) => {
        if (cancelled) return;
        const geom = new STLLoader().parse(buf);
        const mat = new THREE.MeshStandardMaterial({ color: 0xe9e4dc, roughness: 0.55, metalness: 0.05, wireframe, flatShading: false });
        const mesh = new THREE.Mesh(geom, mat);
        scene.add(mesh);
        ctx.current.mesh = mesh;
        fit("perspective");
        onLoaded?.();
      })
      .catch((e) => !cancelled && onError?.(e.message));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return <div ref={host} className="absolute inset-0" data-testid="viewport-canvas" />;
});
